import { BOARDS, DEFAULT_BOARD_KEY } from './boards.js';
import type { BoardLayout, Waypoint } from './boards.js';

// Balance tables for Maze TD. Transcribed verbatim from the frozen prototype
// (prototypes/classic-html/gemtd-classic.html, "Balance" section); the golden
// replay suite depends on every value here. A change to any number invalidates
// recorded replays -- bump nothing casually; balance changes re-record the corpus.

export const NX = 56;
export const NY = 32;
export const CELL = 20;

/** Fixed sim timestep. Replays depend on this. */
export const DT = 1 / 30;

/** Replay format shipped by the prototype; the golden corpus uses it. */
export const REPLAY_VERSION_1 = 'gemtd-replay-1';
/** Replay format v2 (adds balanceHash). Runs under v1/v2 end at wave 50. */
export const REPLAY_VERSION_2 = 'mazetd-replay-2';
/**
 * Replay format written by this app: endless semantics (ADR 0007). The run
 * no longer ends at wave 50 -- it ends at death or a voluntary retire -- and
 * the slate ops exist. v1/v2 replays re-simulate under the legacy
 * end-at-LAST_WAVE rule so the pre-endless leaderboard stays verifiable.
 */
export const REPLAY_VERSION_3 = 'mazetd-replay-3';

export const START_GOLD = 100;
export const START_LIVES = 20;
export const LAST_WAVE = 50;
export const CREEPS_PER_WAVE = 10;
export const SPAWN_GAP = 0.7;

export const TIERS = ['Chipped', 'Flawed', 'Normal', 'Flawless', 'Perfect', 'Great'] as const;
export const TIER_COLORS = [
  '#9aa3b5',
  '#5fd068',
  '#4f9cf0',
  '#b06ae0',
  '#ff9e3d',
  '#ffd257',
] as const;
export const TIER_MULT = [1, 2, 3.5, 6, 10, 22] as const;

export type ChanceRow = readonly [number, number, number, number, number];
export const CHANCE_TABLE: readonly ChanceRow[] = [
  [100, 0, 0, 0, 0],
  [70, 30, 0, 0, 0],
  [50, 40, 10, 0, 0],
  [35, 40, 20, 5, 0],
  [20, 40, 30, 10, 0],
  [10, 35, 35, 18, 2],
  [5, 25, 40, 25, 5],
  [0, 15, 40, 35, 10],
];
export const CHANCE_COSTS = [120, 200, 320, 500, 800, 1300, 2100] as const;

export const FLY_WAVES: ReadonlySet<number> = new Set([5, 8, 15, 22, 29, 36, 43, 30]);
export const SUPERFAST: ReadonlySet<number> = new Set([4, 17, 27, 37]);

/**
 * Endless-mode parameters, waves > LAST_WAVE only -- waves 1-50 are untouched
 * so pre-endless replays and leaderboard entries stay comparable. Growth
 * factor chosen in playtests/endless-scaling.md (ADR 0007).
 */
export const ENDLESS = {
  /** HP multiplier per wave past LAST_WAVE (continues from the wave-50 value). */
  HP_GROWTH: 1.05,
  /** Every MEGA_EVERY-th wave past 50 (100, 150, ...) is a mega boss. */
  MEGA_EVERY: 50,
  MEGA_HP_MULT: 20,
  MEGA_SPEED: 1.6,
  MEGA_BOUNTY_MULT: 30,
  /** Lives lost when a mega boss leaks (regular boss: 5). */
  MEGA_LEAK: 10,
  /** Past 50, waves ending in this digit are superfast. */
  SUPERFAST_DIGIT: 7,
} as const;

export type GemTypeKey =
  'ruby' | 'sapphire' | 'emerald' | 'diamond' | 'amethyst' | 'opal' | 'topaz' | 'aquamarine';

export type GemShape = 'hex' | 'oval' | 'octa' | 'diamond' | 'spark' | 'circle' | 'tri' | 'tridown';

export interface GemDef {
  readonly name: string;
  readonly color: string;
  readonly shape: GemShape;
  readonly dmg: number;
  readonly range: number;
  readonly cd: number;
}

export const GEMS: Readonly<Record<GemTypeKey, GemDef>> = {
  ruby: { name: 'Ruby', color: '#e0333c', shape: 'hex', dmg: 6, range: 3.2, cd: 1.0 },
  sapphire: { name: 'Sapphire', color: '#3b6fe0', shape: 'oval', dmg: 4, range: 3.4, cd: 1.0 },
  emerald: { name: 'Emerald', color: '#2ecc71', shape: 'octa', dmg: 4, range: 3.2, cd: 1.0 },
  diamond: { name: 'Diamond', color: '#eef4ff', shape: 'diamond', dmg: 8, range: 3.6, cd: 0.9 },
  amethyst: { name: 'Amethyst', color: '#9b59d0', shape: 'spark', dmg: 5, range: 4.6, cd: 0.6 },
  opal: { name: 'Opal', color: '#8be8d8', shape: 'circle', dmg: 2, range: 3.0, cd: 1.0 },
  topaz: { name: 'Topaz', color: '#f4c531', shape: 'tri', dmg: 5, range: 3.2, cd: 1.2 },
  aquamarine: {
    name: 'Aquamarine',
    color: '#59c1d8',
    shape: 'tridown',
    dmg: 3,
    range: 2.8,
    cd: 0.9,
  },
};

/**
 * Draft roll order depends on this exact sequence (index = floor(rand() * 8));
 * it is the prototype's Object.keys(GEMS) order.
 */
export const GEM_KEYS: readonly GemTypeKey[] = [
  'ruby',
  'sapphire',
  'emerald',
  'diamond',
  'amethyst',
  'opal',
  'topaz',
  'aquamarine',
];

/** Slow multiplier applied by sapphire at tier t (fraction of speed retained). */
export function slowF(t: number): number {
  return Math.max(0.3, 0.75 - t * 0.09);
}
/** Poison damage per second applied by emerald at tier t (ignores armor). */
export function poisonDps(t: number): number {
  return 2 * Math.pow(2, t);
}
/** Aquamarine attack cooldown at tier t. */
export function aquaCd(t: number): number {
  return Math.max(0.3, 0.9 - t * 0.12);
}

export type SpecialKey =
  'malachite' | 'starruby' | 'silver' | 'jade' | 'blackopal' | 'pinkdiamond' | 'gold' | 'paraiso';

export interface SpecialDef {
  readonly name: string;
  readonly color: string;
  readonly dmg: number;
  readonly range: number;
  readonly cd: number;
  readonly multi?: number;
  readonly splash?: number;
  readonly vsAir?: number;
  readonly slow?: number;
  readonly poison?: number;
  readonly aura?: number;
  readonly crit?: number;
  readonly critMult?: number;
  readonly bounty?: number;
  readonly desc: string;
}

export const SPECIALS: Readonly<Record<SpecialKey, SpecialDef>> = {
  malachite: {
    name: 'Malachite',
    color: '#31b38a',
    dmg: 35,
    range: 3.4,
    cd: 0.7,
    multi: 6,
    desc: 'Hits 6 targets',
  },
  starruby: {
    name: 'Star Ruby',
    color: '#ff5470',
    dmg: 90,
    range: 3.6,
    cd: 0.9,
    splash: 1.8,
    desc: 'Huge cleave (100%)',
  },
  silver: {
    name: 'Silver',
    color: '#cfd8e3',
    dmg: 70,
    range: 5.2,
    cd: 0.8,
    vsAir: 3,
    slow: 0.35,
    desc: 'Anti-air, slows 35%',
  },
  jade: {
    name: 'Jade',
    color: '#57c785',
    dmg: 45,
    range: 3.6,
    cd: 0.75,
    multi: 3,
    poison: 30,
    desc: 'Poisons 3 targets',
  },
  blackopal: {
    name: 'Black Opal',
    color: '#6a5a9e',
    dmg: 100,
    range: 3.4,
    cd: 0.8,
    multi: 3,
    aura: 0.5,
    desc: 'Multi-hit + 50% speed aura',
  },
  pinkdiamond: {
    name: 'Pink Diamond',
    color: '#ff9ecb',
    dmg: 260,
    range: 4.0,
    cd: 0.85,
    crit: 0.35,
    critMult: 3,
    splash: 1.3,
    desc: 'Crits x3, splash',
  },
  gold: {
    name: 'Gold',
    color: '#ffd700',
    dmg: 200,
    range: 3.8,
    cd: 0.8,
    bounty: 3,
    desc: '+3 gold per kill it lands',
  },
  paraiso: {
    name: 'Paraiso',
    color: '#66ffcc',
    dmg: 400,
    range: 4.2,
    cd: 0.7,
    multi: 4,
    poison: 150,
    desc: 'Devastating poison, 4 targets',
  },
};

export type RecipeComponent = readonly [GemTypeKey, number];
export interface Recipe {
  readonly key: SpecialKey;
  readonly comps: readonly [RecipeComponent, RecipeComponent, RecipeComponent];
}

export const RECIPES: readonly Recipe[] = [
  {
    key: 'malachite',
    comps: [
      ['emerald', 0],
      ['aquamarine', 0],
      ['opal', 0],
    ],
  },
  {
    key: 'starruby',
    comps: [
      ['ruby', 1],
      ['amethyst', 0],
      ['diamond', 0],
    ],
  },
  {
    key: 'silver',
    comps: [
      ['amethyst', 1],
      ['diamond', 1],
      ['sapphire', 0],
    ],
  },
  {
    key: 'jade',
    comps: [
      ['emerald', 1],
      ['topaz', 1],
      ['opal', 0],
    ],
  },
  {
    key: 'blackopal',
    comps: [
      ['opal', 2],
      ['sapphire', 1],
      ['topaz', 1],
    ],
  },
  {
    key: 'pinkdiamond',
    comps: [
      ['diamond', 3],
      ['ruby', 2],
      ['amethyst', 2],
    ],
  },
  {
    key: 'gold',
    comps: [
      ['topaz', 3],
      ['emerald', 2],
      ['diamond', 2],
    ],
  },
  {
    key: 'paraiso',
    comps: [
      ['emerald', 3],
      ['aquamarine', 3],
      ['opal', 2],
    ],
  },
];

// ---- slates (the endgame layer; ADR 0008) ------------------------------------
// Adapted from Gem TD+'s slate system (playtests/gem-td-plus-reference.md;
// permission per its header): a junk-roll sink. A slate is forged from one
// Normal gem of its anchor type plus two Flawed gems of any type, and must be
// socketed into an existing rock -- the board's accumulated dead mass becomes
// the endgame resource. Base slates pair-combine into greater slates. All
// effects are deterministic radius passives with no new RNG draws, so
// pre-slate replays re-simulate bit-exactly.

export type SlateKey =
  | 'lodestone'
  | 'whetstone'
  | 'hourglass'
  | 'ember'
  | 'beacon'
  | 'prism'
  | 'gravestone'
  | 'hollow'
  | 'bastion'
  | 'wraithstone'
  | 'elderstone'
  | 'viperstone';

export interface SlateDef {
  readonly name: string;
  readonly color: string;
  /** Effect radius in cells. */
  readonly radius: number;
  /** Damage multiplier vs flying for gems in radius. */
  readonly vsAir?: number;
  /** Armor shred added per landed hit from gems in radius (capped at the creep's armor). */
  readonly shred?: number;
  /** Speed multiplier for creeps in radius (utility slow; stacks with attack slows, floored). */
  readonly zoneSlow?: number;
  /** Poison damage rate multiplier for creeps in radius. */
  readonly poisonRate?: number;
  /** Range bonus (cells) for gems in radius. */
  readonly rangeBonus?: number;
  /** Multiplier on attack-speed aura rates emitted from inside the radius. */
  readonly auraMult?: number;
  /** Creeps dying in radius damage creeps within SPITE_RADIUS for this fraction of max HP. */
  readonly spite?: number;
  /** Damage-taken multiplier for creeps in radius. */
  readonly dmgTaken?: number;
  readonly desc: string;
}

export const SLATES: Readonly<Record<SlateKey, SlateDef>> = {
  lodestone: {
    name: 'Lodestone',
    color: '#8fa3c8',
    radius: 6,
    vsAir: 1.5,
    desc: 'Gems in range deal +50% to flying',
  },
  whetstone: {
    name: 'Whetstone',
    color: '#d8985a',
    radius: 6,
    shred: 1,
    desc: 'Hits from gems in range shred 1 armor',
  },
  hourglass: {
    name: 'Hourglass',
    color: '#e0c878',
    radius: 6,
    zoneSlow: 0.8,
    desc: 'Creeps in range slowed 20%',
  },
  ember: {
    name: 'Ember',
    color: '#ff7a45',
    radius: 6,
    poisonRate: 1.25,
    desc: 'Poison ticks 25% faster in range',
  },
  beacon: {
    name: 'Beacon',
    color: '#bfe3ff',
    radius: 6,
    rangeBonus: 0.5,
    desc: 'Gems in range gain +0.5 range',
  },
  prism: {
    name: 'Prism',
    color: '#c78be0',
    radius: 6,
    auraMult: 1.5,
    desc: 'Speed auras in range count x1.5',
  },
  gravestone: {
    name: 'Gravestone',
    color: '#aab2bd',
    radius: 6,
    spite: 0.1,
    desc: 'Deaths in range wound nearby creeps',
  },
  hollow: {
    name: 'Hollow',
    color: '#4f8f88',
    radius: 6,
    dmgTaken: 1.1,
    desc: 'Creeps in range take +10% damage',
  },
  bastion: {
    name: 'Bastion',
    color: '#e08a4a',
    radius: 7,
    shred: 2,
    dmgTaken: 1.2,
    desc: 'Shreds 2 armor, +20% damage taken',
  },
  wraithstone: {
    name: 'Wraithstone',
    color: '#9fd8e8',
    radius: 7,
    zoneSlow: 0.7,
    auraMult: 2,
    desc: '30% zone slow, auras count x2',
  },
  elderstone: {
    name: 'Elderstone',
    color: '#9fce56',
    radius: 7,
    poisonRate: 1.6,
    spite: 0.25,
    desc: 'Poison x1.6, deaths wound harder',
  },
  viperstone: {
    name: 'Viperstone',
    color: '#e8e05a',
    radius: 7,
    vsAir: 1.75,
    rangeBonus: 1,
    desc: '+75% vs flying, +1 range',
  },
};

/** Deterministic iteration/index order (feeds stateHash and the UI). */
export const SLATE_KEYS: readonly SlateKey[] = [
  'lodestone',
  'whetstone',
  'hourglass',
  'ember',
  'beacon',
  'prism',
  'gravestone',
  'hollow',
  'bastion',
  'wraithstone',
  'elderstone',
  'viperstone',
];

export interface SlateRecipe {
  readonly key: SlateKey;
  /** One Normal (tier 2) gem of this type, plus SLATE_FODDER_COUNT Flawed of any type. */
  readonly anchor: GemTypeKey;
}

/** Each gem type anchors exactly one base slate. */
export const SLATE_RECIPES: readonly SlateRecipe[] = [
  { key: 'gravestone', anchor: 'ruby' },
  { key: 'hourglass', anchor: 'sapphire' },
  { key: 'ember', anchor: 'emerald' },
  { key: 'beacon', anchor: 'diamond' },
  { key: 'whetstone', anchor: 'amethyst' },
  { key: 'prism', anchor: 'opal' },
  { key: 'lodestone', anchor: 'topaz' },
  { key: 'hollow', anchor: 'aquamarine' },
];

export interface GreaterSlate {
  readonly key: SlateKey;
  readonly pair: readonly [SlateKey, SlateKey];
}

/** Base slates pair-combine into greater slates (order-insensitive). */
export const GREATER_SLATES: readonly GreaterSlate[] = [
  { key: 'bastion', pair: ['whetstone', 'hollow'] },
  { key: 'wraithstone', pair: ['hourglass', 'prism'] },
  { key: 'elderstone', pair: ['ember', 'gravestone'] },
  { key: 'viperstone', pair: ['lodestone', 'beacon'] },
];

export const SLATE_ANCHOR_TIER = 2; // Normal
export const SLATE_FODDER_TIER = 1; // Flawed
export const SLATE_FODDER_COUNT = 2;
/** Same-key slates must sit at least this far apart (cells) -- anti-stack. */
export const SLATE_MIN_DIST = 8;
/** Gravestone/Elderstone spite splash radius (cells). */
export const SPITE_RADIUS = 2;

// ---- board layout (waypoint tour) -------------------------------------------
// The tour lives in boards.ts; these live bindings always reflect the selected
// layout. Selection happens once at boot (before any Game is constructed) --
// waypoints feed balanceHash, so replays are self-describing per layout.

export type { Waypoint, BoardLayout } from './boards.js';

let board: BoardLayout = BOARDS.find((b) => b.key === DEFAULT_BOARD_KEY)!;

export let WAYPOINTS: readonly Waypoint[] = board.waypoints;

/** Flyer tour: every checkpoint in the layout's own crisscross order. */
export let FLY_ROUTE: readonly Waypoint[] = board.flyOrder.map((i) => board.waypoints[i]!);

export let WAYPOINT_SET: ReadonlySet<string> = new Set(WAYPOINTS.map((w) => `${w.x},${w.y}`));

export function currentBoard(): BoardLayout {
  return board;
}

/**
 * Select a board layout by key (call before constructing a Game; existing
 * Game instances keep the paths they computed). Returns the layout, or null
 * for an unknown key (selection unchanged).
 */
export function selectBoard(key: string): BoardLayout | null {
  const next = BOARDS.find((b) => b.key === key);
  if (!next) return null;
  board = next;
  WAYPOINTS = board.waypoints;
  FLY_ROUTE = board.flyOrder.map((i) => board.waypoints[i]!);
  WAYPOINT_SET = new Set(WAYPOINTS.map((w) => `${w.x},${w.y}`));
  return board;
}

export const SPEEDS = [1, 2, 3, 5, 10] as const;
